import {
  Table,
  Header,
  SpaceBetween,
  Pagination,
  Button,
  Popover,
} from "@amzn/awsui-components-react";
import { useState, useMemo } from "react";
import { getColumnDefinitions, RedriveItem } from "./redriveItemsTableConfig";
import { generateRedriveExecutionUrl } from "../../../helpers/logUtils";
import { Stage } from "../../../lusca-search-api/generated-src";
import { RedriveKind } from "../redriveKinds";

interface RedriveItemsTableProps {
  kind: RedriveKind;
  items: RedriveItem[];
  loading: boolean;
  executionId: string;
  stage: Stage;
  user?: string;
  isTruncated?: boolean;
  downloadUrl?: string | null;
}

const PAGE_SIZE = 50;

export function RedriveItemsTable({
  kind,
  items,
  loading = false,
  executionId,
  stage,
  user,
  isTruncated = false,
  downloadUrl,
}: RedriveItemsTableProps) {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const columnDefinitions = useMemo(() => getColumnDefinitions(kind), [kind]);

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPageIndex - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    // Items were redriven in the execution's Dev stack.
    return items
      .slice(startIndex, endIndex)
      .map((item) => (user ? { ...item, user } : item));
  }, [items, currentPageIndex, user]);

  const totalPages = Math.ceil(items.length / PAGE_SIZE);

  return (
    <Table
      resizableColumns
      stickyHeader
      columnDefinitions={columnDefinitions}
      items={paginatedItems}
      loading={loading}
      loadingText={`Loading ${kind.name.toLowerCase()}s`}
      selectionType="multi"
      header={
        <Header
          variant="awsui-h1-sticky"
          counter={items.length > 0 ? `(${items.length})` : undefined}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              {downloadUrl && (
                <Button
                  onClick={() => window.open(downloadUrl, "_blank")}
                  variant="normal"
                >
                  Download CSV
                </Button>
              )}
              <Button
                onClick={() =>
                  window.open(
                    generateRedriveExecutionUrl(
                      stage,
                      executionId,
                      kind.stateMachineName,
                      user
                    ),
                    "_blank"
                  )
                }
                variant="primary"
              >
                View in Console
              </Button>
            </SpaceBetween>
          }
          info={
            isTruncated ? (
              <span style={{ fontSize: "0.8em", color: "#666" }}>
                (
                <Popover
                  dismissButton={false}
                  position="bottom"
                  size="medium"
                  triggerType="custom"
                  content={`Only the first 400KB of the ${kind.name.toLowerCase()}s file was loaded. The full file may contain additional ${kind.name.toLowerCase()}s. View in Console for complete list.`}
                >
                  <Button
                    iconName="status-warning"
                    variant="icon"
                    ariaLabel="Truncation warning"
                  />
                </Popover>
                truncated)
              </span>
            ) : undefined
          }
        >
          {kind.name}s in Redrive Execution {executionId}
        </Header>
      }
      empty={
        <SpaceBetween size="m" alignItems="center">
          <div>No {kind.name.toLowerCase()}s found for this execution</div>
        </SpaceBetween>
      }
      pagination={
        items.length > PAGE_SIZE ? (
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) =>
              setCurrentPageIndex(detail.currentPageIndex)
            }
          />
        ) : undefined
      }
    />
  );
}
